// ./dialogs/ExportAllOrderItemsDialog.js
// Product summary across all clients for a month / date range: shown on screen,
// printable, and exportable to Excel. Screen, print and Excel all follow the
// same "split by unit price" toggle (sent to the backend as `group_by`).
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  LinearProgress,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { startOfMonth, endOfMonth, subMonths, format } from 'date-fns';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import SummarizeIcon from '@mui/icons-material/Summarize';
import API from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { useIsPhone } from '../../hooks/useIsMobile';
import { openPrintWindow, generateBusinessHeader } from '../../utils/printUtils';

const SPLIT_BY_PRICE_STORAGE_KEY = 'productSummary.splitByPrice';

const RANGE_MODES = {
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  MONTH: 'month',
  CUSTOM: 'custom',
};

// Format date as YYYY-MM-DD using local timezone (never toISOString — shifts Israel dates)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date) => format(date, 'dd/MM/yyyy');

const formatNumber = (value) =>
  Number(value || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 });

const formatCurrency = (value) =>
  `₪${Number(value || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const readSplitPreference = () => {
  try {
    return window.localStorage.getItem(SPLIT_BY_PRICE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeSplitPreference = (value) => {
  try {
    window.localStorage.setItem(SPLIT_BY_PRICE_STORAGE_KEY, String(value));
  } catch {
    // Storage unavailable (private mode etc.) — preference just isn't remembered
  }
};

// Decode MIME encoded-word Content-Disposition (same as ExportOrderItemsDialog)
const decodeMimeEncodedWord = (str) => {
  const mimeMatch = str.match(/=\?(.+?)\?([bBqQ])\?(.+?)\?=/);
  if (!mimeMatch) return str;
  const [, charset, encoding, encodedText] = mimeMatch;
  if (encoding.toLowerCase() === 'b') {
    try {
      const decodedBytes = atob(encodedText);
      const uint8Array = new Uint8Array(decodedBytes.length);
      for (let i = 0; i < decodedBytes.length; i++) {
        uint8Array[i] = decodedBytes.charCodeAt(i);
      }
      return new TextDecoder(charset).decode(uint8Array);
    } catch (error) {
      console.error('Error decoding MIME word:', error);
      return str;
    }
  }
  return str;
};

const filenameFromDisposition = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;
  if (contentDisposition.includes('=?') && contentDisposition.includes('?=')) {
    const match = decodeMimeEncodedWord(contentDisposition).match(/filename="?([^";\n]+)"?/);
    return match?.[1] || fallback;
  }
  const starMatch = contentDisposition.match(/filename\*=([^;]+)/);
  if (starMatch?.[1]) {
    const parts = starMatch[1].split("''");
    if (parts.length === 2) return decodeURIComponent(parts[1]);
  }
  const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  return match?.[1] ? match[1].replace(/['"]/g, '') : fallback;
};

const fetchProductSummary = async ({ startDate, endDate, groupBy }) => {
  const response = await API.get('/orders/products-summary/', {
    params: { start_date: startDate, end_date: endDate, group_by: groupBy },
  });
  return response.data;
};

const datePickerTextFieldSx = {
  width: { xs: '100%', md: 200 },
  minWidth: { xs: 0, md: 200 },
  '& .MuiOutlinedInput-root': { direction: 'ltr' },
};

const ExportAllOrderItemsDialog = ({
  open,
  onClose,
  orderPluralLabel = 'הזמנות',
  productLabel = 'מוצר',
  productPluralLabel = 'מוצרים',
}) => {
  const { userProfile } = useUser();
  const isPhone = useIsPhone();

  const [rangeMode, setRangeMode] = useState(RANGE_MODES.THIS_MONTH);
  const [selectedMonth, setSelectedMonth] = useState(() => startOfMonth(new Date()));
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(() => new Date());
  const [splitByPrice, setSplitByPrice] = useState(readSplitPreference);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'total_quantity', direction: 'desc' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const errorRef = useRef(null);

  // Reset to "this month" each time the dialog opens
  useEffect(() => {
    if (open) {
      setRangeMode(RANGE_MODES.THIS_MONTH);
      setSelectedMonth(startOfMonth(new Date()));
      setCustomStart(null);
      setCustomEnd(new Date());
      setSearch('');
      setExportError('');
    }
  }, [open]);

  const range = useMemo(() => {
    const today = new Date();
    switch (rangeMode) {
      case RANGE_MODES.THIS_MONTH:
        return { start: startOfMonth(today), end: endOfMonth(today) };
      case RANGE_MODES.LAST_MONTH: {
        const last = subMonths(today, 1);
        return { start: startOfMonth(last), end: endOfMonth(last) };
      }
      case RANGE_MODES.MONTH:
        return selectedMonth && !Number.isNaN(selectedMonth.getTime())
          ? { start: startOfMonth(selectedMonth), end: endOfMonth(selectedMonth) }
          : { start: null, end: null };
      default:
        return { start: customStart, end: customEnd };
    }
  }, [rangeMode, selectedMonth, customStart, customEnd]);

  const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());
  const rangeError = useMemo(() => {
    if (!isValidDate(range.start)) return rangeMode === RANGE_MODES.CUSTOM ? 'יש לבחור תאריך התחלה' : '';
    if (!isValidDate(range.end)) return 'יש לבחור תאריך סיום';
    if (range.start > range.end) return 'תאריך ההתחלה לא יכול להיות אחרי תאריך הסיום';
    return '';
  }, [range, rangeMode]);
  const rangeReady = isValidDate(range.start) && isValidDate(range.end) && !rangeError;

  const groupBy = splitByPrice ? 'product_price' : 'product';
  const queryParams = rangeReady
    ? { startDate: formatDateLocal(range.start), endDate: formatDateLocal(range.end), groupBy }
    : null;

  const { data, isLoading, isFetching, error: queryError } = useQuery({
    queryKey: ['orders', 'products-summary', queryParams],
    queryFn: () => fetchProductSummary(queryParams),
    enabled: open && rangeReady,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const fetchError = queryError ? (queryError?.response?.data?.error || 'שגיאה בטעינת הנתונים') : '';
  const visibleError = exportError || fetchError;

  useEffect(() => {
    if (visibleError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [visibleError]);

  const rows = useMemo(() => {
    const products = data?.products || [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? products.filter((p) => (p.name || '').toLowerCase().includes(term))
      : products;
    const { key, direction } = sort;
    const factor = direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        const cmp = String(av || '').localeCompare(String(bv || ''), 'he');
        return cmp !== 0 ? cmp * factor : (a.unit_price || 0) - (b.unit_price || 0);
      }
      const cmp = (av || 0) - (bv || 0);
      return cmp !== 0 ? cmp * factor : String(a.name).localeCompare(String(b.name), 'he');
    });
  }, [data, search, sort]);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleSplitToggle = (event) => {
    setSplitByPrice(event.target.checked);
    writeSplitPreference(event.target.checked);
  };

  const periodText = rangeReady
    ? `${formatDisplayDate(range.start)} - ${formatDisplayDate(range.end)}`
    : '';

  const columns = useMemo(() => {
    const cols = [
      { key: 'name', label: productLabel, align: 'right' },
      { key: 'unit', label: 'יחידה', align: 'center' },
    ];
    if (splitByPrice) {
      cols.push({ key: 'unit_price', label: 'מחיר יחידה', align: 'center', render: formatCurrency });
    }
    cols.push(
      { key: 'total_quantity', label: 'כמות', align: 'center', render: formatNumber, bold: true },
      { key: 'total_amount', label: 'סה"כ', align: 'center', render: formatCurrency },
      { key: 'orders_count', label: orderPluralLabel, align: 'center', render: formatNumber },
    );
    return cols;
  }, [splitByPrice, productLabel, orderPluralLabel]);

  const handlePrint = useCallback(() => {
    const businessProfile = userProfile?.group?.business_profile || {};
    const headerCells = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('');
    const bodyRows = rows
      .map(
        (row) =>
          `<tr>${columns
            .map((c) => {
              const value = c.render ? c.render(row[c.key]) : row[c.key];
              return `<td class="${c.align === 'right' ? 'text' : 'num'}">${escapeHtml(value)}</td>`;
            })
            .join('')}</tr>`
      )
      .join('');
    const searchNote = search.trim() ? ` · סינון: "${escapeHtml(search.trim())}"` : '';
    const html = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
<meta charset="utf-8" />
<title>סיכום ${escapeHtml(productPluralLabel)}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 24px; color: #111; }
  h1 { font-size: 22px; margin: 0 0 6px 0; }
  .meta { font-size: 14px; color: #444; margin-bottom: 14px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { background: #f0f0f0; border: 1px solid #999; padding: 6px; }
  td { border: 1px solid #bbb; padding: 5px 6px; }
  td.num { text-align: center; }
  td.text { text-align: right; }
  tfoot td { font-weight: bold; background: #fafafa; }
</style>
</head>
<body>
  ${generateBusinessHeader(businessProfile)}
  <h1>סיכום ${escapeHtml(productPluralLabel)}${splitByPrice ? ' לפי מחיר יחידה' : ''}</h1>
  <div class="meta">תקופה: ${escapeHtml(periodText)} · ${escapeHtml(orderPluralLabel)}: ${formatNumber(data?.total_orders)} · לקוחות: ${formatNumber(data?.unique_clients)}${searchNote}</div>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;
    openPrintWindow(html);
  }, [columns, rows, userProfile, productPluralLabel, orderPluralLabel, splitByPrice, periodText, data, search]);

  const handleExcel = async () => {
    if (!queryParams) return;
    setExporting(true);
    setExportError('');
    try {
      const response = await API.get('/orders/export-all-items-summary/', {
        params: {
          start_date: queryParams.startDate,
          end_date: queryParams.endDate,
          group_by: queryParams.groupBy,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromDisposition(
          response.headers['content-disposition'],
          `products_summary_${queryParams.startDate}_${queryParams.endDate}.xlsx`
        )
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setExportError('שגיאה ביצוא הנתונים. אנא נסה שוב.');
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (!exporting) onClose();
  };

  const hasRows = rows.length > 0;
  const showInitialLoader = isLoading && !data;

  const rangeChips = [
    { mode: RANGE_MODES.THIS_MONTH, label: 'החודש' },
    { mode: RANGE_MODES.LAST_MONTH, label: 'חודש קודם' },
    { mode: RANGE_MODES.MONTH, label: 'בחירת חודש' },
    { mode: RANGE_MODES.CUSTOM, label: 'טווח מותאם' },
  ];

  const renderTable = () => (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: { md: '55vh' } }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align} sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                <TableSortLabel
                  active={sort.key === col.key}
                  direction={sort.key === col.key ? sort.direction : 'desc'}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.product_id}-${row.unit_price}`} hover>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={col.bold ? { fontWeight: 'bold', fontSize: '1rem' } : undefined}
                >
                  {col.render ? col.render(row[col.key]) : row[col.key] || '—'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCards = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.map((row) => (
        <Paper
          key={`${row.product_id}-${row.unit_price}`}
          variant="outlined"
          sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{row.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {splitByPrice ? `${formatCurrency(row.unit_price)} · ` : ''}
              {formatCurrency(row.total_amount)} · {formatNumber(row.orders_count)} {orderPluralLabel}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'left', flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem', lineHeight: 1.2 }}>
              {formatNumber(row.total_quantity)}
            </Typography>
            {row.unit && (
              <Typography variant="caption" color="text.secondary">
                {row.unit}
              </Typography>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <MobileDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 }, minHeight: { sm: '70vh' } } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <SummarizeIcon sx={{ color: 'primary.main' }} />
            <Typography component="span" variant="h6" sx={{ fontWeight: 'bold' }}>
              סיכום {productPluralLabel}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={exporting} sx={{ color: 'grey.500' }} aria-label="סגירה">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, px: { xs: 1.5, sm: 3 } }}>
          {/* Range selection */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, mt: 1 }}>
            {rangeChips.map((chip) => (
              <Chip
                key={chip.mode}
                label={chip.label}
                color={rangeMode === chip.mode ? 'primary' : 'default'}
                variant={rangeMode === chip.mode ? 'filled' : 'outlined'}
                onClick={() => setRangeMode(chip.mode)}
              />
            ))}
          </Box>

          {(rangeMode === RANGE_MODES.MONTH || rangeMode === RANGE_MODES.CUSTOM) && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(2, max-content)' },
                gap: 2,
                mb: 2,
              }}
            >
              {rangeMode === RANGE_MODES.MONTH ? (
                <DatePicker
                  label="חודש"
                  views={['year', 'month']}
                  openTo="month"
                  format="MM/yyyy"
                  value={selectedMonth}
                  onChange={(value) => setSelectedMonth(value)}
                  slotProps={{ textField: { sx: { ...datePickerTextFieldSx, gridColumn: { xs: '1 / -1', md: 'auto' } } } }}
                />
              ) : (
                <>
                  <DatePicker
                    label="מתאריך"
                    format="dd/MM/yyyy"
                    value={customStart}
                    onChange={(value) => setCustomStart(value)}
                    slotProps={{ textField: { required: true, sx: datePickerTextFieldSx } }}
                  />
                  <DatePicker
                    label="עד תאריך"
                    format="dd/MM/yyyy"
                    value={customEnd}
                    onChange={(value) => setCustomEnd(value)}
                    slotProps={{ textField: { sx: datePickerTextFieldSx } }}
                  />
                </>
              )}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 2,
            }}
          >
            <TextField
              placeholder={`חיפוש ${productLabel}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', md: 260 }, minWidth: { xs: 0, md: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={<Switch checked={splitByPrice} onChange={handleSplitToggle} />}
              label="פיצול לפי מחיר יחידה"
              sx={{ mx: 0 }}
            />
          </Box>

          {visibleError && (
            <Alert ref={errorRef} severity="error" sx={{ mb: 2 }} onClose={exportError ? () => setExportError('') : undefined}>
              {visibleError}
            </Alert>
          )}

          {rangeError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {rangeError}
            </Alert>
          )}

          {rangeReady && data && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 2 },
                mb: 1.5,
                p: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                <strong>תקופה:</strong> {periodText}
              </Typography>
              <Typography variant="body2">
                <strong>{orderPluralLabel}:</strong> {formatNumber(data.total_orders)}
              </Typography>
              <Typography variant="body2">
                <strong>לקוחות:</strong> {formatNumber(data.unique_clients)}
              </Typography>
              <Typography variant="body2">
                <strong>{productPluralLabel}:</strong> {formatNumber(new Set((data.products || []).map((p) => p.product_id)).size)}
              </Typography>
            </Box>
          )}

          <Box sx={{ height: 4, mb: 1 }}>{isFetching && !showInitialLoader && <LinearProgress />}</Box>

          {showInitialLoader ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rangeReady && data && !hasRows ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              {search.trim()
                ? `לא נמצאו ${productPluralLabel} התואמים לחיפוש`
                : `לא נמכרו ${productPluralLabel} בתקופה שנבחרה`}
            </Typography>
          ) : rangeReady && hasRows ? (
            isPhone ? renderCards() : renderTable()
          ) : null}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={handlePrint}
            variant="outlined"
            startIcon={<PrintIcon />}
            disabled={!rangeReady || !hasRows || isFetching}
            sx={{ direction: 'ltr' }}
          >
            הדפסה
          </Button>
          <Button
            onClick={handleExcel}
            variant="contained"
            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            disabled={!rangeReady || exporting}
            sx={{ direction: 'ltr' }}
          >
            {exporting ? 'מייצא...' : 'יצוא לאקסל'}
          </Button>
        </DialogActions>
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default ExportAllOrderItemsDialog;
